import {useLazyQuery} from "@apollo/client";
import {ForwardedRef, forwardRef, ReactNode, useCallback} from "react";
import {FiExternalLink, FiSearch} from "react-icons/fi";
import {
  components,
  DropdownIndicatorProps,
  GroupBase,
  NoticeProps,
  OptionProps,
  OptionsOrGroups,
  SelectInstance,
  SingleValueProps,
} from "react-select";
import {useAsyncPaginate, useComponents} from "react-select-async-paginate";

import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {useStableLoadingState} from "@/components/select/useStableLoadingState.ts";
import {UPTIME_FEEDBACK_URL} from "@/lib/constants/links.ts";
import {CloudStatusGroupList, GQL_THIRD_PARTY_GROUPS, ThirdPartyService} from "@/lib/queries/checks.ts";

import styles from "@/pages/checks/components/form/fields/components/ThirdPartyGroup.module.scss";

export const ThirdPartyOption =
  <T extends {logo_src?: string}>(nameField: keyof T) =>
  ({data, children, ...props}: OptionProps<T>) => (
    <components.Option {...props} data={data}>
      <div className="d-flex align-items-center flex-wrap row-gap-2">
        <CDNImage className={styles.componentIconL} alt={`${data[nameField]} logo`} src={data.logo_src} />
        <div className="pulseTextBodySmallMedium">{children}</div>
      </div>
    </components.Option>
  );

export const ThirdPartyValue =
  <T,>(placeholder?: ReactNode) =>
  (props: SingleValueProps<T>) => (
    <components.SingleValue {...props}>
      <div className="pulse-color-gray-500">{placeholder}</div>
    </components.SingleValue>
  );

export const ThirdPartyIndicatorSeparator = () => null;

export const ThirdPartyDropdownIndicator = <T,>(props: DropdownIndicatorProps<T>) => (
  <components.DropdownIndicator {...props}>
    <Icon icon={FiSearch} className="pulse-color-gray-500" />
  </components.DropdownIndicator>
);

export const ThirdPartyNoOptionsMessage =
  <T,>(itemName: string) =>
  (props: NoticeProps<T>) => (
    <components.NoOptionsMessage {...props}>
      <Icon icon={FiSearch} className="pulse-color-gray-500 mb-4" removeDivDimensions />
      <div className="pulseTextH3Bold pulse-color-gray-800 mb-2">No {itemName} found.</div>
      <a target="_blank" href={UPTIME_FEEDBACK_URL} className="pulseTextBody pulse-color-gray-700">
        Request a new {itemName} <Icon icon={FiExternalLink} display="inline" className="pulse-color-gray-900" />
      </a>
    </components.NoOptionsMessage>
  );

export const ThirdPartyGroupSelect = forwardRef(
  (props: SelectProps<ThirdPartyService>, ref: ForwardedRef<SelectInstance<ThirdPartyService>>) => {
    const [getGroups] = useLazyQuery<CloudStatusGroupList>(GQL_THIRD_PARTY_GROUPS);
    const loadOptions = useCallback(
      async (search: string, prevOptions: OptionsOrGroups<ThirdPartyService, GroupBase<ThirdPartyService>>) => {
        const {data} = await getGroups({variables: {search, limit: 20, offset: prevOptions.length}});
        const items = data?.cloudStatusGroups?.items || [];
        const total = data?.cloudStatusGroups?.total || 0;
        return {options: items, hasMore: prevOptions.length + items.length < total};
      },
      [getGroups]
    );
    const paginateResult = useStableLoadingState(
      useAsyncPaginate<ThirdPartyService, GroupBase<ThirdPartyService>, undefined>(
        {loadOptions, debounceTimeout: 500},
        []
      )
    );
    const paginateComponents = useComponents<ThirdPartyService, GroupBase<ThirdPartyService>, false>({
      Option: ThirdPartyOption<ThirdPartyService>("group"),
      SingleValue: ThirdPartyValue<ThirdPartyService>(props.placeholder),
      IndicatorSeparator: ThirdPartyIndicatorSeparator,
      DropdownIndicator: ThirdPartyDropdownIndicator<ThirdPartyService>,
      NoOptionsMessage: ThirdPartyNoOptionsMessage<ThirdPartyService>("service"),
    });
    return (
      <Select
        ref={ref}
        getOptionValue={({id}) => id.toString()}
        getOptionLabel={({group}) => group}
        isSearchable
        components={paginateComponents}
        {...props}
        {...paginateResult}
      />
    );
  }
);
